import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { MinimalAlert, MinimalButton } from '@ovasabi/ui-minimal'
import { ChowModal, Money, Stepper } from '../../components/chow'
import { CheckIcon, ClockIcon, PlateIcon } from '../../components/icons'
import { useMarketplaceOrderSnapshot } from '../../generated/prototypeRuntime'
import { isLiveRuntime } from '../../lib/runtime'
import type { StoreRecord } from './chefShared'
import { ChowThumb } from './ChowThumb'
import { useChooseChow } from './ChooseChowContext'
import { useChefKitchens, type UiDish } from './projections'
import {
  addPlanToCart,
  mealsPerDay,
  parseKey,
  planDish,
  planPreview,
  planWindow,
  relativeDay,
  type PlanSlot,
} from './mealPlanning'

/**
 * Plan one dish onto a day, from wherever that dish is being looked at.
 *
 * The meal planner asks day-first: open a day, search the menu, pick something.
 * That is the right shape when you are filling a week, and the wrong one when
 * you are already looking at the dish you want — the customer had to leave the
 * dish, open the planner, choose a day and find the same dish again.
 *
 * So this is the same flow read backwards: dish-first, day second. It is
 * deliberately NOT a second planning feature. The day window, the confirm-by
 * date limits, kitchen availability, and reservation writes all
 * come from mealPlanning, which the calendar reads too — so a meal planned here
 * lands on the calendar as an ordinary hold, on identical terms, and both
 * screens open and close the same days at the same moment.
 */

const Style = {
  Days: styled.div`
    display: grid;
    gap: 8px;
    max-height: 48vh;
    overflow-y: auto;
    margin: 6px 0 2px;
    padding-right: 2px;
  `,
  Day: styled(MinimalButton)<{ $blocked?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    width: 100%;
    padding: 12px 14px;
    border: 1px solid ${minimalVars.color.borderSubtle};
    border-radius: ${minimalVars.radius.md};
    background: ${minimalVars.color.bgSurface};
    color: ${minimalVars.color.textPrimary};
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: border-color 140ms ease, background-color 140ms ease;
    opacity: ${({ $blocked }) => ($blocked ? 0.55 : 1)};
    &:disabled {
      cursor: not-allowed;
    }
    &:not(:disabled):hover {
      border-color: ${minimalVars.color.brand};
      background: ${minimalVars.color.bgSurfaceHover};
    }
  `,
  DayMain: styled.span`
    display: grid;
    gap: 2px;
    min-width: 0;
  `,
  DayName: styled.span`
    font-weight: ${minimalVars.typography.weightSemibold};
  `,
  DaySub: styled.span`
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
  `,
  DayTail: styled.span`
    display: grid;
    justify-items: end;
    gap: 2px;
    flex-shrink: 0;
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
  `,
  Review: styled.div`
    display: grid;
    gap: ${minimalVars.space.xs};
  `,
  ReviewTop: styled.div`
    display: flex;
    align-items: center;
    gap: ${minimalVars.space.xs};
  `,
  Thumb: styled.div`
    width: 56px;
    height: 56px;
    border-radius: ${minimalVars.radius.md};
    overflow: hidden;
    flex-shrink: 0;
  `,
  Title: styled.div`
    display: grid;
    gap: 2px;
    min-width: 0;
  `,
  Name: styled.span`
    font-weight: ${minimalVars.typography.weightSemibold};
  `,
  Note: styled.div`
    display: grid;
    gap: 4px;
    padding: 10px 12px;
    border-radius: ${minimalVars.radius.md};
    background: ${minimalVars.color.bgSurfaceAlt};
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
  `,
  Warn: styled.span`
    color: var(--minimal-color-warning, #e0a008);
    font-weight: ${minimalVars.typography.weightSemibold};
  `,
  QtyRow: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
  `,
  Actions: styled.div`
    display: flex;
    justify-content: flex-end;
    gap: ${minimalVars.space['2xs']};
  `,
  Done: styled.div`
    display: grid;
    gap: ${minimalVars.space.xs};
    justify-items: center;
    text-align: center;
    padding: ${minimalVars.space.xs} 0;
  `,
  DoneMark: styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 999px;
    background: ${minimalVars.color.brandSoft};
    color: ${minimalVars.color.brand};
  `,
  Gate: styled.div`
    display: grid;
    gap: ${minimalVars.space.xs};
    padding: ${minimalVars.space['2xs']} 0;

    p {
      margin: 0;
      color: ${minimalVars.color.textSecondary};
      line-height: 1.5;
    }
  `,
}

export interface PlanDishModalProps {
  open: boolean
  dish: UiDish
  /** Plates to start from — the dish page's own quantity, so the sheet opens on
      what the customer already chose rather than resetting to one. */
  initialQuantity?: number
  onClose: () => void
}

export const PlanDishModal = ({
  open,
  dish,
  initialQuantity = 1,
  onClose,
}: PlanDishModalProps) => {
  const navigate = useNavigate()
  const { profileId } = useChooseChow()
  const kitchens = useChefKitchens()
  const orders = useMarketplaceOrderSnapshot() as { records: readonly StoreRecord[] }

  const [dayKey, setDayKey] = useState('')
  const [qty, setQty] = useState(initialQuantity)
  const [planning, setPlanning] = useState(false)
  const [error, setError] = useState('')
  const [planned, setPlanned] = useState<{ dayKey: string; quantity: number; orderId: string; totalMinor: number } | null>(null)

  // Every open is a fresh decision: a stale day or a leftover confirmation from
  // the last time this sheet was used would be answering a question the
  // customer did not just ask.
  useEffect(() => {
    if (!open) return
    setDayKey('')
    setQty(initialQuantity)
    setPlanning(false)
    setError('')
    setPlanned(null)
  }, [open, initialQuantity, dish.id])

  const myOrders = useMemo(
    () =>
      orders.records.filter((o) =>
        isLiveRuntime() ? String(o.customer_profile_id ?? '') === profileId : true,
      ),
    [orders.records, profileId],
  )
  const planned_ = useMemo(() => mealsPerDay(myOrders), [myOrders])

  const slots = useMemo(() => planWindow(), [])
  const kitchen = kitchens.get(dish.chefId)

  /** A day the dish itself cannot be cooked on is offered but flagged — the
      calendar warns rather than blocks, and a chef who takes the order anyway
      should not be prevented from doing so here. */
  const cautionFor = (slot: PlanSlot) => planPreview(dish, slot.date, kitchen).caution

  const existingPlatesOnDay = useMemo(() => {
    if (!dayKey) return 0
    let count = 0
    for (const o of myOrders) {
      const scheduled = String(o.scheduled_at ?? '').slice(0, 10)
      if (scheduled === dayKey) {
        const meta = (o.metadata ?? {}) as Record<string, unknown>
        const name = String(meta.dish_name ?? o.summary ?? o.item_name ?? '').toLowerCase()
        if (name.includes(dish.name.toLowerCase())) {
          count += Number(meta.quantity ?? o.quantity ?? 1)
        }
      }
    }
    return count
  }, [dayKey, myOrders, dish.name])

  const preview = dayKey ? planPreview(dish, parseKey(dayKey), kitchen) : null

  const confirm = async () => {
    if (!dayKey || planning) return

    setPlanning(true)
    setError('')
    const outcome = await planDish({ dish, dayKey, quantity: qty, profileId })
    setPlanning(false)
    if (!outcome.ok) {
      setError(outcome.error)
      return
    }
    setPlanned({ dayKey, quantity: qty, orderId: outcome.orderId, totalMinor: outcome.totalMinor })
  }

  const title = planned
    ? 'Meal planned'
    : dayKey
      ? 'Confirm this meal'
      : `Plan ${dish.name}`

  return (
    <>
      <ChowModal open={open} title={title} onClose={onClose}>
        {error && (
          <MinimalAlert tone="danger" title="Could not plan that meal">
            {error}
          </MinimalAlert>
        )}

        {planned ? (
          <Style.Done>
            <Style.DoneMark aria-hidden>
              <CheckIcon size="sm" />
            </Style.DoneMark>
            <div>
              <Style.Name>
                {dish.name}
                {planned.quantity > 1 ? ` ×${planned.quantity}` : ''}
              </Style.Name>
              <Style.DaySub>
                {' '}
                is planned for{' '}
                {parseKey(planned.dayKey).toLocaleDateString(undefined, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}
                .
              </Style.DaySub>
            </div>
            <Style.DaySub>
              It is saved and unpaid. Add it to your cart to pay with your other meals.
            </Style.DaySub>
            <Style.Actions>
              <MinimalButton
                size="sm"
                variant="ghost"
                onClick={() => {
                  // Back to the day list, not to the day just planned — that
                  // day's confirm step is the one screen this button is not
                  // asking for, and the list now shows it as taken.
                  setPlanned(null)
                  setDayKey('')
                }}
              >
                Plan another day
              </MinimalButton>
              <MinimalButton size="sm" onClick={async () => {
                const outcome = await addPlanToCart([{ dish, dishName: dish.name, quantity: planned.quantity,
                  holdId: planned.orderId, balanceMinor: planned.totalMinor }], profileId)
                if (!outcome.ok) { setError(outcome.error); return }
                onClose()
                navigate('/cart')
              }}>Add to cart</MinimalButton>
              <MinimalButton
                size="sm"
                variant="ghost"
                onClick={() => {
                  onClose()
                  navigate('/mealplan')
                }}
              >
                View meal plan
              </MinimalButton>
            </Style.Actions>
          </Style.Done>
        ) : preview ? (
          <Style.Review>
            <Style.ReviewTop>
              <Style.Thumb>
                <ChowThumb
                  src={dish.image}
                  emoji={dish.glyph}
                  alt={dish.name}
                  name={dish.name}
                  category={dish.category}
                />
              </Style.Thumb>
              <Style.Title>
                <Style.Name>{dish.name}</Style.Name>
                <Style.DaySub>by {dish.chef}</Style.DaySub>
              </Style.Title>
            </Style.ReviewTop>

            <Style.Note>
              <span>
                <strong>{preview.when}</strong> · {preview.time}
              </span>
              <span>
                {preview.from}
                {preview.prep && ` · ${preview.prep}`}
              </span>
              {preview.caution && <Style.Warn>{preview.caution}</Style.Warn>}
              {existingPlatesOnDay > 0 && (
                <Style.DaySub style={{ color: 'var(--minimal-color-brand, #2b303b)', fontWeight: 500 }}>
                  Already have {existingPlatesOnDay} {existingPlatesOnDay === 1 ? 'plate' : 'plates'} of {dish.name} planned for this day.
                  Adding {qty} more will make it {existingPlatesOnDay + qty} plates.
                </Style.DaySub>
              )}
            </Style.Note>

            <Style.QtyRow>
              <span>Plates</span>
              <Stepper value={qty} onChange={setQty} min={1} max={200} />
              <Money amount={dish.price * qty} tone="brand" size="sm" />
            </Style.QtyRow>

            <Style.Actions>
              <MinimalButton size="sm" variant="ghost" onClick={() => setDayKey('')}>
                Choose another day
              </MinimalButton>
              <MinimalButton size="sm" loading={planning} disabled={planning} onClick={confirm}>
                Plan this meal
              </MinimalButton>
            </Style.Actions>
          </Style.Review>
        ) : (
          <>
            <Style.DaySub>
              Save a meal for a day, then add it to your cart for payment.
            </Style.DaySub>
            <Style.Days>
              {slots.map((slot) => {
                const count = planned_.get(slot.key) ?? 0
                const caution = cautionFor(slot)
                return (
                  <Style.Day variant="quiet"
                    key={slot.key}
                    type="button"
                    disabled={slot.expired}
                    $blocked={slot.expired || Boolean(caution)}
                    aria-label={`Plan ${dish.name} for ${slot.weekday} ${slot.label}`}
                    onClick={() => setDayKey(slot.key)}
                  >
                    <Style.DayMain>
                      <Style.DayName>
                        {slot.weekday} · {slot.label}
                      </Style.DayName>
                      {/* "Today · 9 Sept" followed by "today" says the same
                          thing twice; the relative line only earns its place
                          once the weekday stops being self-explanatory. */}
                      {(() => {
                        const sub = slot.expired
                          ? 'Ordering closed'
                          : caution
                            ? caution
                            : relativeDay(slot.date)
                        return sub === slot.weekday.toLowerCase() ? null : (
                          <Style.DaySub>{sub}</Style.DaySub>
                        )
                      })()}
                    </Style.DayMain>
                    <Style.DayTail>
                      {count > 0 && (
                        <span>
                          <PlateIcon size="sm" /> {count} planned
                        </span>
                      )}
                      {!slot.expired && count === 0 && (
                        <span>
                          <ClockIcon size="sm" /> Open
                        </span>
                      )}
                    </Style.DayTail>
                  </Style.Day>
                )
              })}
            </Style.Days>
          </>
        )}
      </ChowModal>

    </>
  )
}
